import { styled } from '@compiled/react';
import isEqual from 'lodash/isEqual';
import type { FC } from 'react';
import React, { memo, useMemo } from 'react';

import type { ADNode } from '@atlaskit/editor-common/validator';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes';
import { extensionToADF } from '@confluence/fabric-extension-lib/entry-points/editor-extensions';
import { expVal } from '@confluence/feature-experiments';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';

import {
	FlatStyleTocItemContainer,
	ListStyleTocItemContainer,
	ListStyleTocLevelContainer,
} from './TableOfContentsElementStructure';
import { handleTocNode } from './handleADF';
// eslint-disable-next-line import/no-cycle
import { generateTocTreeStructure } from './generateTocTreeStructure';
// eslint-disable-next-line import/no-cycle
import { TableOfContentsComponent } from './TableOfContentsComponent';
import { useCurrentAdf } from './useCurrentAdf';

export const nonPrintableWrapperTestId = 'non-printable-wrapper';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const NonPrintableWrapper = styled.div({
	overflow: 'auto',
	'@media print': {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'ul ul:not(:first-of-type), ol ul:not(:first-of-type)': {
		marginTop: '0px',
	},
});

export const printableWrapperTestId = 'printable-wrapper';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const PrintableWrapper = styled.div({
	overflow: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'ul ul:not(:first-of-type), ol ul:not(:first-of-type)': {
		marginTop: '0px',
	},
});

export interface ElementFactoryInterface {
	createTocLevelContainer: Function;
	createTocItemContainer: Function;
	decorateToc?: Function;
}

export interface SsrElementContentInterface {
	text: string;
	type: string;
	innerHTML?: string;
}

export interface SsrElementInterface {
	type: string;
	id?: string;
	attrs: {
		level: number;
		startPos?: string;
	};
	content: SsrElementContentInterface[];
}

export interface TableOfContentsInterface {
	adf: ADNode | string | null;
	cloudId: string;
	userId: string;
	macroDefaultProps: ExtensionHandlerProps;
	isInEditMode?: boolean;
}

export const EmptyTocItem = 'toc-empty-item';

export function getSeparators(separator: string) {
	switch (separator) {
		case 'braces':
			return ['{ ', ' } { ', ' }'];
		case 'pipe':
			return ['', ' | ', ''];
		case 'parens':
			return ['( ', ' ) ( ', ' )'];
		case '':
		case 'brackets':
			// brackets in the ADF is an empty string
			return ['[ ', ' ] [ ', ' ]'];
		default:
			// Match separator usage for custom separators
			return ['', separator, ''];
	}
}

const TableOfContentsContainer: FC<TableOfContentsInterface> = (props) => {
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const { cloudId, userId, adf, macroDefaultProps, isInEditMode = false } = props;
	const { node, mode, contentId, extensionKey } = macroDefaultProps;

	const currentAdf = useCurrentAdf(adf, isInEditMode);
	const macroNode = extensionToADF(node);
	const tocOptions = handleTocNode(macroNode, currentAdf);
	const attributes = getMacroAttributesFromADFNode(node);
	const name = getExperienceName(mode, node);

	let ElementFactory: ElementFactoryInterface;
	let elementsArray: object[] | null;
	if (tocOptions.structure === 'flat') {
		ElementFactory = flatTocElementFactory();
	} else {
		ElementFactory = listTocElementFactory();
	}

	try {
		elementsArray = generateTocTreeStructure(tocOptions.headerelements, [], cloudId, userId);
	} catch (error) {
		return (
			<MacroExperienceFailure
				name={name}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				error={error || new Error(`${extensionKey} failed to render`)}
				attributes={attributes}
				source="TableOfContents"
			/>
		);
	}

	const separators: any = getSeparators(tocOptions.separators || '');
	const initialSeparatorRef = { current: true };

	const TocContent = () => {
		const flatSeparators =
			tocOptions.structure === 'flat'
				? {
						'data-preseparator': separators[0],
						'data-midseparator': separators[1],
						'data-postseparator': separators[2],
					}
				: {};
		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={tocOptions.className}
				data-numberedoutline={tocOptions.outline}
				data-cssliststyle={tocOptions.cssliststyle}
				data-csslistindent={tocOptions.csslistindent}
				data-headerelements={tocOptions.headerRange}
				data-hasbody={tocOptions.hasbody}
				data-macro-name={tocOptions.name}
				data-macro-id={tocOptions.macroId}
				data-structure={tocOptions.structure}
				data-vc="toc"
				{...flatSeparators}
				{...(renderServerPlaceholder
					? { 'data-ssr-placeholder': `toc-${tocOptions.macroId}` }
					: { 'data-ssr-placeholder-replace': `toc-${tocOptions.macroId}` })}
			>
				<TableOfContentsComponent
					elementsArray={elementsArray}
					elementFactory={ElementFactory}
					config={tocOptions}
					separators={separators}
					initialSeparatorRef={initialSeparatorRef}
					isInEditMode={isInEditMode}
				/>
				<MacroExperienceSuccess
					name={name}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
				/>
			</div>
		);
	};

	return tocOptions.printable === 'false' ? (
		<NonPrintableWrapper
			title="Macro (toc)"
			data-fabric-macro={tocOptions.macroId}
			data-macro-body
			data-macro-parameters={JSON.stringify(macroNode?.attrs?.parameters?.macroParams)}
			data-testid={nonPrintableWrapperTestId}
			data-vc="non-printable-toc-wrapper"
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': tocOptions.macroId }
				: { 'data-ssr-placeholder-replace': tocOptions.macroId })}
		>
			<TocContent />
		</NonPrintableWrapper>
	) : (
		<PrintableWrapper
			title="Macro (toc)"
			data-fabric-macro={tocOptions.macroId}
			data-macro-body
			data-macro-parameters={JSON.stringify(macroNode?.attrs?.parameters?.macroParams)}
			data-testid={printableWrapperTestId}
			data-vc="printable-toc-wrapper"
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': tocOptions.macroId }
				: { 'data-ssr-placeholder-replace': tocOptions.macroId })}
		>
			<TocContent />
		</PrintableWrapper>
	);
};

const MemoizedTableOfContentsContainer: FC<TableOfContentsInterface> = ({
	cloudId,
	userId,
	adf,
	macroDefaultProps,
	isInEditMode = false,
}) => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { node, mode, contentId, extensionKey } = macroDefaultProps;

	const currentAdf = useCurrentAdf(adf, isInEditMode);
	const macroNode = useMemo(() => extensionToADF(node), [node]);
	const tocOptions = useMemo(() => handleTocNode(macroNode, currentAdf), [macroNode, currentAdf]);
	const name = getExperienceName(mode, node);

	const ElementFactory = useMemo(
		() => (tocOptions.structure === 'flat' ? flatTocElementFactory() : listTocElementFactory()),
		[tocOptions.structure],
	);

	const separators = useMemo(
		() => getSeparators(tocOptions.separators || ''),
		[tocOptions.separators],
	);
	const initialSeparatorRef = useMemo(() => ({ current: true }), []);
	initialSeparatorRef.current = true;

	const [preseparator, midseparator, postseparator] = separators;
	const flatSeparators = useMemo(
		() =>
			tocOptions.structure === 'flat'
				? {
						'data-preseparator': preseparator,
						'data-midseparator': midseparator,
						'data-postseparator': postseparator,
					}
				: {},
		[tocOptions.structure, preseparator, midseparator, postseparator],
	);

	const { elementsArray, error } = useMemo(() => {
		try {
			return {
				elementsArray: generateTocTreeStructure(tocOptions.headerelements, [], cloudId, userId),
				error: null,
			};
		} catch (error) {
			return {
				elementsArray: null,
				error,
			};
		}
	}, [tocOptions?.headerelements, cloudId, userId]);
	if (error) {
		const attributes = getMacroAttributesFromADFNode(node);

		return (
			<MacroExperienceFailure
				name={name}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				error={error || new Error(`${extensionKey} failed to render`)}
				attributes={attributes}
				source="TableOfContents"
			/>
		);
	}

	const tocContent = (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={tocOptions.className}
			data-numberedoutline={tocOptions.outline}
			data-cssliststyle={tocOptions.cssliststyle}
			data-csslistindent={tocOptions.csslistindent}
			data-headerelements={tocOptions.headerRange}
			data-hasbody={tocOptions.hasbody}
			data-macro-name={tocOptions.name}
			data-macro-id={tocOptions.macroId}
			data-structure={tocOptions.structure}
			data-vc="toc"
			{...flatSeparators}
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': `toc-${tocOptions.macroId}` }
				: { 'data-ssr-placeholder-replace': `toc-${tocOptions.macroId}` })}
		>
			<TableOfContentsComponent
				elementsArray={elementsArray}
				elementFactory={ElementFactory}
				config={tocOptions}
				separators={separators}
				initialSeparatorRef={initialSeparatorRef}
				isInEditMode={isInEditMode}
			/>
			<MacroExperienceSuccess
				name={name}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
			/>
		</div>
	);

	// Wrap tocContent for printable:
	let Wrapper: typeof NonPrintableWrapper | typeof PrintableWrapper;
	let wrapperTestId: typeof nonPrintableWrapperTestId | typeof printableWrapperTestId;
	let wrapperVc: string;
	if (tocOptions.printable === 'false') {
		Wrapper = NonPrintableWrapper;
		wrapperTestId = nonPrintableWrapperTestId;
		wrapperVc = 'non-printable-toc-wrapper';
	} else {
		Wrapper = PrintableWrapper;
		wrapperTestId = printableWrapperTestId;
		wrapperVc = 'printable-toc-wrapper';
	}

	return (
		<Wrapper
			title="Macro (toc)"
			data-fabric-macro={tocOptions.macroId}
			data-macro-body
			data-macro-parameters={JSON.stringify(macroNode?.attrs?.parameters?.macroParams)}
			data-testid={wrapperTestId}
			data-vc={wrapperVc}
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': tocOptions.macroId }
				: { 'data-ssr-placeholder-replace': tocOptions.macroId })}
		>
			{tocContent}
		</Wrapper>
	);
};

/**
 * Creates HTML elements for use in building a flat-style TOC
 */
function flatTocElementFactory() {
	return {
		createTocLevelContainer() {
			return FlatStyleTocItemContainer;
		},
		createTocItemContainer() {
			return FlatStyleTocItemContainer;
		},
		decorateToc() {
			return true;
		},
	};
}

/**
 * Creates HTML elements for use in building a list-style TOC
 */
function listTocElementFactory() {
	return {
		createTocLevelContainer() {
			return ListStyleTocLevelContainer;
		},
		createTocItemContainer() {
			return ListStyleTocItemContainer;
		},
	};
}

const TableOfContentsContainerWithExp = (props) => {
	const TableOfContentsContainerComponent = expVal(
		'cc_complexit_fe_toc_without_ssr',
		'MemoizedTableOfContentsContainer',
		false,
	)
		? MemoizedTableOfContentsContainer
		: TableOfContentsContainer;
	return <TableOfContentsContainerComponent {...props} />;
};

export const TableOfContents = memo(
	withErrorBoundary({
		attribution: Attribution.BACKBONE,
	})(TableOfContentsContainerWithExp),
	isEqual,
);
